import { useState, useEffect, useCallback, useRef } from "react";
import type { Session } from "@/services/SessionsService";
import { SessionsService } from "@/services/SessionsService";
import { SessionCard } from "./SessionCard";
import { SessionDetailsModal } from "./SessionDetailsModal";

interface SessionsTabProps {
  userId: number;
  onCountChange?: (count: number) => void;
  onSessionSelect?: (sessionId: number | null) => void;
}

const SESSIONS_PER_PAGE = 10;

export const SessionsTab: React.FC<SessionsTabProps> = ({ userId, onCountChange, onSessionSelect }) => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);

  // Filters
  const [boardTypeFilter, setBoardTypeFilter] = useState<string>("");
  const [photoOnlyFilter, setPhotoOnlyFilter] = useState(false);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const loadSessions = useCallback(async (pageNum: number, reset: boolean = false) => {
    if (pageNum === 0) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);

    try {
      const data = await SessionsService.listUserSessions(userId, pageNum, {
        boardtype: boardTypeFilter || undefined,
        photoonly: photoOnlyFilter || undefined,
      });

      if (reset) {
        setSessions(data);
      } else {
        setSessions((prev) => [...prev, ...data]);
      }

      // Check if we have more data
      setHasMore(data.length >= SESSIONS_PER_PAGE);

      // Update count on first load
      if (pageNum === 0 && onCountChange) {
        // Note: This is just the first page count, actual total would need a separate API
        onCountChange(data.length);
      }
    } catch (err) {
      console.error("Error loading sessions:", err);
      setError("Failed to load sessions. Please try again.");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [userId, boardTypeFilter, photoOnlyFilter, onCountChange]);

  // Initial load
  useEffect(() => {
    setPage(0);
    setSessions([]);
    loadSessions(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, boardTypeFilter, photoOnlyFilter]);

  // Debug: log sessions when they change
  useEffect(() => {
    if (sessions.length > 0) {
      console.log("First session object:", sessions[0]);
      console.log("Session keys:", Object.keys(sessions[0]));
    }
  }, [sessions]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !isLoadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadSessions(nextPage, false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoading, isLoadingMore, page, loadSessions]);

  const handleRefresh = () => {
    setPage(0);
    setSessions([]);
    loadSessions(0, true);
  };

  const handleSessionClick = (sessionId: number) => {
    console.log("Session clicked:", sessionId);
    setSelectedSessionId(sessionId);
    onSessionSelect?.(sessionId);
  };

  const handleSessionClose = () => {
    setSelectedSessionId(null);
    onSessionSelect?.(null);
  };

  // Handle deep link for session modal
  useEffect(() => {
    const deeplinkSessionId = sessionStorage.getItem("deeplink-sessionId");
    if (deeplinkSessionId) {
      const parsedId = parseInt(deeplinkSessionId, 10);
      if (!isNaN(parsedId)) {
        setSelectedSessionId(parsedId);
        onSessionSelect?.(parsedId);
      }
      sessionStorage.removeItem("deeplink-sessionId");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading && sessions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-cyan-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (error && sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <svg className="w-12 h-12 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-sm text-gray-600 mb-4">{error}</p>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 text-sm font-medium text-cyan-600 hover:text-cyan-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {/* Board Type Filter */}
          <select
            value={boardTypeFilter}
            onChange={(e) => setBoardTypeFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">All Board Types</option>
            <option value="twintip">Twin Tip</option>
            <option value="directional">Directional</option>
            <option value="foil">Foil</option>
            <option value="landboarding">Landboarding</option>
            <option value="snowkiting">Snowkiting</option>
            <option value="windsurfing">Windsurfing</option>
            <option value="wingfoiling">Wing Foiling</option>
          </select>

          {/* Photo Only Filter */}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={photoOnlyFilter}
              onChange={(e) => setPhotoOnlyFilter(e.target.checked)}
              className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
            />
            With Photos Only
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (window.confirm("This will export all user data including all sessions, jumps, and GPS tracks. Continue?")) {
                window.open(SessionsService.getGdprExportUrl(userId), "_blank");
              }
            }}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            title="Export all user data (GDPR)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            GDPR Export
          </button>

          <button
            onClick={handleRefresh}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Sessions Grid */}
      {sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="text-sm font-medium text-gray-600 mb-1">No sessions found</p>
          <p className="text-xs text-gray-500">
            {boardTypeFilter || photoOnlyFilter
              ? "Try adjusting your filters"
              : "This user hasn't recorded any sessions yet."}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sessions.map((session) => (
              <SessionCard
                key={session.ID}
                session={session}
                onClick={() => handleSessionClick(session.ID)}
              />
            ))}
          </div>

          {/* Load More / Infinite Scroll Trigger */}
          <div ref={loadMoreRef} className="py-4">
            {isLoadingMore && (
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm">Loading more sessions...</span>
              </div>
            )}
            {!hasMore && sessions.length > 0 && (
              <p className="text-center text-sm text-gray-400">
                No more sessions to load
              </p>
            )}
          </div>
        </>
      )}

      {/* Session Details Modal */}
      {selectedSessionId && (
        <SessionDetailsModal
          isOpen={true}
          onClose={handleSessionClose}
          sessionId={selectedSessionId}
          userId={userId}
        />
      )}
    </div>
  );
};
